<template>
  <div space-y-4>
    <div grid grid-flow-dense sm:grid-cols-2 gap-2>
      <DataItem col-span-2 :copyOnClick="false" :loading="isLoading" label="Address" :value="data?.address">
        <template v-if="isLoading">
          <br />
          <br />
        </template>
        <template v-else>
          <span text-xl sm:text-2xl>{{ data?.address }}</span>
          <a
            text-lg
            text-brand-blue
            block
            mt-2
            class="after:content-['_↗']"
            hover:opacity-50
            v-if="data?.address"
            :href="`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.address)}`"
            target="_blank"
            >Open in Google Maps</a
          >
        </template>
      </DataItem>
      <DataItem
        :copyOnClick="false"
        col-span-2
        sm:col-span-1
        :loading="isLoading"
        label="Phone Number"
        :value="phoneNumberLink"
      >
        <a
          text-xl
          sm:text-2xl
          underline
          underline-dashed
          underline-2
          underline-offset-2
          underline-blue
          hover:text-neutral-400
          v-if="phoneNumberLink"
          :href="phoneNumberLink"
        >
          {{ phoneNumber }}
        </a>
      </DataItem>
      <DataItem
        :copyOnClick="false"
        col-span-2
        sm:col-span-1
        break-words
        :loading="isLoading"
        label="Website"
        :value="data?.website"
        valueClass="text-xl sm:text-2xl"
      >
        <a
          text-xl
          sm:text-2xl
          underline
          underline-dashed
          underline-2
          underline-offset-2
          underline-blue
          hover:text-neutral-400
          v-if="data?.website"
          :href="data?.website"
          target="_blank"
        >
          {{
            data?.website
              .replace(/https?:\/\//, "")
              .replace(/\/$/, "")
              .toLowerCase()
          }}
        </a>
      </DataItem>
      <DataItem
        :copyOnClick="false"
        col-span-2
        sm:col-span-1
        :loading="isLoading"
        label="Schools"
        :value="data?.numSchools"
      >
        <span text-xl sm:text-2xl>{{ data?.numSchools }}</span>
        <a
          text-lg
          text-brand-blue
          mt-2
          block
          class="after:content-['_↗']"
          hover:opacity-50
          :href="`https://nces.ed.gov/ccd/schoolsearch/school_list.asp?Search=1&DistrictID=${district.id}`"
          target="_blank"
          >{{ data?.numSchools == 1 ? "View school" : "View all schools" }}</a
        >
      </DataItem>
    </div>
    <div v-if="quickLinks">
      <h3 text-2xl font-semibold text-stone-500 mt-10 mb-4>Quick Links</h3>
      <ul space-y-4>
        <li v-for="quickLink in quickLinks">
          <a
            rel="noreferrer"
            :href="quickLink.url"
            class="text-brand-blue font-bold block hover:opacity-50 after:content-['_↗']"
          >
            {{ quickLink.label }}
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>
<script setup>
const props = defineProps({
  district: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const { data, pending } = await useFetchSchoolDistrictDetails({ id: props.district.id });

const isLoading = computed(() => props.loading || pending.value);

const phoneNumber = computed(() => {
  if (!data.value?.phone) {
    return null;
  }
  const phone = data.value.phone.replace(/\D/g, "");
  return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
});

const phoneNumberLink = computed(() => {
  if (!data.value?.phone) {
    return null;
  }
  return `tel:+1${data.value.phone.replace(/\D/g, "")}`;
});

const website = computed(() => {
  if (!data.value?.website) {
    return null;
  }
  return data.value?.website
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "")
    .toLowerCase();
});

function getSchoolYear(date = new Date()) {
  let currentAcademicYear = date.getFullYear();
  if (date.getMonth() < 5) {
    currentAcademicYear -= 1;
  }
  return `${currentAcademicYear}-${currentAcademicYear + 1}`;
}

const quickLinks = computed(() => {
  const quickLinks = [];
  if (!website.value) {
    return quickLinks;
  }
  const academicYear = getSchoolYear();
  const calendarQuery = encodeURIComponent(`! ${academicYear} academic calendar site:${website.value}`);
  quickLinks.push({
    label: `${academicYear} Academic Calendar`,
    url: `https://duckduckgo.com/?q=${calendarQuery}`,
  });

  const registrationQuery = encodeURIComponent(`! enrollment registration site:${website.value}`);
  quickLinks.push({
    label: "Enrollment/Registration Information",
    url: `https://duckduckgo.com/?q=${registrationQuery}`,
  });

  const menuQuery = encodeURIComponent(`! menu site:${website.value}`);
  quickLinks.push({
    label: "School Lunch Menu",
    url: `https://duckduckgo.com/?q=${menuQuery}`,
  });

  const sportsQuery = encodeURIComponent(`! athletics site:${website.value}`);

  quickLinks.push({
    label: "Sports and Athletics",
    url: `https://duckduckgo.com/?q=${sportsQuery}`,
  });

  const staffQuery = encodeURIComponent(`! staff directory site:${website.value}`);
  quickLinks.push({
    label: "Staff Directory",
    url: `https://duckduckgo.com/?q=${staffQuery}`,
  });

  return quickLinks;
});
</script>
